'use client'

import Text from '@/components/Text/Text'
import useEvent from '@/hooks/useEvent'
import { type IClause } from '@/lib/ris-types'
import { useStore } from '@/lib/store/store'
import { deterministicUrlify } from '@/lib/utils'
import { useSpring } from 'framer-motion'
import Link from 'next/link'
import { type FunctionComponent, memo, useEffect, type RefObject, useContext, type CSSProperties, createContext, type MutableRefObject } from 'react'
import { useVirtualizer, type VirtualItem, type Virtualizer } from '@tanstack/react-virtual'
import { handleClauseVisibilityChange, highlightedClauseStore } from '../Clause/scroll-store'
import * as styles from './Sidebar.css'
import { writable } from 'svelte/store'
import { observe } from 'react-intersection-observer'

export const TocContext = createContext<{ ref: MutableRefObject<HTMLDivElement | null>, onSelect?: () => void }>({ ref: { current: null } })

interface TocProps {
  clauses: IClause[]
}

let stopped = false
let firstScroll: boolean = true
export const scrollHintStore = writable({ label: '', top: 0, scrollToElement: () => {} })

/**
 * Table of contents
 * TODO: Rename to LawSidebar or something
 */
export default function Toc ({ clauses }: TocProps) {
  const { ref: parentRef, onSelect } = useContext(TocContext)

  const highlightedClause = useStore(highlightedClauseStore)
  const spring = useSpring(0, {
    damping: 15,
    mass: 0.3,
    stiffness: 150,
  })
  useEffect(() => {
    const elem = parentRef.current
    if (elem === null) return
    const unsub = spring.on('change', (v: number) => {
      if (!stopped) {
        elem.scrollTop = v
      }
    })
    function stopAnim (): void {
      const shouldCancel = Math.abs((spring.getPrevious() ?? 0) - spring.get()) <= Math.abs((elem?.scrollTop ?? 0) - spring.get())
      if (shouldCancel) {
        stopped = true
      }
    }
    elem.addEventListener('scroll', stopAnim, { passive: true })
    return () => {
      unsub()
      elem.removeEventListener('scroll', stopAnim)
    }
  }, [spring, parentRef])

  const { scrollToIndex, getTotalSize, getVirtualItems, options } = useVirtualizer({
    count: clauses.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40,
    overscan: 5,
    scrollPaddingStart: 30,
    scrollPaddingEnd: 307,
    initialRect: {
      width: 0,
      height: 1000,
    },
    initialOffset: 0,
    scrollToFn (offsetTarget, { behavior = 'auto' }) {
      stopped = false

      if (parentRef.current === null) return

      if (behavior === 'auto' && parentRef.current !== null) {
        parentRef.current.scrollTop = offsetTarget
        spring.set(parentRef.current.scrollTop, false)
        return
      }

      spring.set(parentRef.current.scrollTop, false)

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          spring.set(offsetTarget, behavior === 'smooth')
        })
      })
    },
  })
  const virtualItems = getVirtualItems()
  useHighlightChange({ highlightedClause, scrollToIndex, virtualItems })

  const handleScrollToElement = useEvent(() => {
    if (!clauses[highlightedClause]?.enumeration) return
    scrollHintStore.set({
      label: clauses[highlightedClause].enumeration,
      top: (window.innerHeight - 63/* NavBar height */ - 18/* hint button height */ - 18/* vertical margin of scrollbar */) / options.count * (highlightedClause - 1),
      scrollToElement () {
        scrollToIndex(highlightedClause, { behavior: 'smooth' })
      },
    })
  })
  useEffect(() => {
    handleScrollToElement()
  }, [highlightedClause, options.count, handleScrollToElement])

  return (
    <div style={{ height: getTotalSize(), flexShrink: 0, position: 'relative' }}>
      {virtualItems.length === 1 && (
        <Text style={{ fontStyle: 'italic', textAlign: 'center', paddingTop: '1em' }} color='secondary'>
          Dieses Dokument hat keine Paragrafen
        </Text>
      )}
      {virtualItems.map(({ start, index }) => {
        if (index === 0) return null // The 0th item we do not render in the sidebar

        const clause = clauses[index]
        return (
          <TocItem
            key={index}
            title={clause.title}
            enumeration={clause.enumeration}
            isHighlighted={highlightedClause === index}
            onSelect={onSelect}
            offset={start}
          />
        )
      })}
    </div>
  )
}

/**
 * Register event handlers on content sections to be included in the sidebar's "active item" highlighting tracking.
 * For each registered item there should be an item rendered in the sidebar as well (1-to-1 matching).
 */
export function useRegisterSidebarHighlighting (ref: RefObject<HTMLDivElement | null>, index) {
  useEffect(() => {
    const elem = ref.current
    if (elem === null) return

    function onChange (inView) {
      handleClauseVisibilityChange({ inView, index })
    }
    return observe(elem, onChange, { rootMargin: '-135px 0px 0px 0px' })
  }, [ref, index])
}

interface HighlightChangeProps {
  highlightedClause: number
  scrollToIndex?: Virtualizer<Window, Element>['scrollToIndex']
  virtualItems: VirtualItem[]
}

function useHighlightChange ({ highlightedClause, scrollToIndex, virtualItems }: HighlightChangeProps) {
  const handleHighlightChange = useEvent(({ forceScroll = false, smoothScroll = true }: { forceScroll?: boolean, smoothScroll?: boolean } = {}) => {
    /**
     * On mobile, when the toc is hidden, the virtualItems array is empty, so we don't need to do anything
     */
    if (virtualItems.length === 0) return

    const index = highlightedClause
    if (forceScroll || (index >= virtualItems[0].index && index <= (virtualItems.at(-1)?.index ?? 0))) {
      if (firstScroll) {
        firstScroll = false
      }
      if (window.wasAfterClick) {
        window.wasAfterClick = false
      }

      scrollToIndex?.(index, {
        align: 'auto',
        behavior: smoothScroll ? 'smooth' : 'auto',
      })
    }
  })

  /** The first scroll should not be animated */
  useEffect(function resetFirstScroll () {
    return () => {
      firstScroll = true
    }
  }, [])

  // Handle scrolling when highlighted element changes
  useEffect(function scrollToHighlightedElement () {
    if (highlightedClause === -1) return

    handleHighlightChange({ forceScroll: firstScroll || window.wasAfterClick, smoothScroll: !firstScroll })
  }, [handleHighlightChange, highlightedClause])
}
interface TocItemProps {
  isHighlighted: boolean
  title?: string
  enumeration?: string
  onSelect?: () => void
  offset?: number
  style?: CSSProperties
  href?: string
  enumerationHash?: string
}
export const TocItem: FunctionComponent<TocItemProps> = memo(function TocItem ({ title, enumeration, isHighlighted, onSelect, offset, style = {}, href }) {
  return (
    <Link
      className={styles.tocItem}
      onClick={() => {
        onSelect?.()
      }}
      style={{
        // Use `top` instead of transform here, so that the scale effect when ":active" still works correctly
        top: offset !== undefined ? offset - 40 : undefined,
        ...style,
      }}
      href={href ?? `#${deterministicUrlify(enumeration ?? title ?? '')}`}
      prefetch={false}
    >
      {(enumeration) && (
        <span className={styles.tocItemEnumeration}>
          {enumeration}
        </span>
      )}
      <Text
        className={`${styles.tocItemText}${isHighlighted ? ' highlighted' : ''}`}
        maxLines={1}
        style={{ marginLeft: (!enumeration) ? -6 : undefined }}
      >
        {title}
        <HighlightingEffect isHighlighted={isHighlighted} />
      </Text>
    </Link>
  )
})

const HighlightingEffect = memo(function HighlightingEffect ({ isHighlighted }: { isHighlighted: boolean }) {
  return (
    <span
      className={`${styles.tocItemHighlightEffect} ${isHighlighted ? 'highlighted' : ''}`}
    />
  )
})
